import { cn } from "#app/utils/misc"
import { ReactNode } from "react"

type TypographyHeadingProps = React.DetailedHTMLProps<
	React.HTMLAttributes<HTMLHeadingElement>,
	HTMLHeadingElement
>

export function TypographyH1(props: TypographyHeadingProps) {
	return (
		<h1
			{...props}
			className={cn(
				"scroll-m-20 font-script text-xl !leading-loose tracking-tight text-primary lg:text-2xl",
				props.className,
			)}
		>
			{props.children}
		</h1>
	)
}

export function TypographyH2(props: TypographyHeadingProps) {
	return (
		<h2
			{...props}
			className={cn(
				"scroll-m-20 border-b pb-2 font-script text-xl leading-relaxed tracking-tight text-primary transition-colors first:mt-0",
				props.className,
			)}
		>
			{props.children}
		</h2>
	)
}

export function TypographyH3(props: TypographyHeadingProps) {
	return (
		<h3
			{...props}
			className={cn(
				"scroll-m-20 font-script text-lg tracking-tight text-primary",
				props.className,
			)}
		>
			{props.children}
		</h3>
	)
}

export function TypographyH4(props: TypographyHeadingProps) {
	return (
		<h4
			{...props}
			className={cn(
				"scroll-m-20 font-script text-base tracking-tight text-primary",
				props.className,
			)}
		>
			{props.children}
		</h4>
	)
}

type TypographyPProps = React.DetailedHTMLProps<
	React.HTMLAttributes<HTMLParagraphElement>,
	HTMLParagraphElement
>

export function TypographyP(props: TypographyPProps) {
	return (
		<p
			{...props}
			className={cn("leading-7 [&:not(:first-child)]:mt-3", props.className)}
		>
			{props.children}
		</p>
	)
}

// type TypographyListProps = {
// 	items: (string | ReactNode)[]
// }

// export function TypographyList({ items }: TypographyListProps) {
// 	return (
// 		<ul className="my-6 ml-6 list-disc [&>li]:mt-2">
// 			{items.map((item, index) => (
// 				<li key={index}>{item}</li>
// 			))}
// 		</ul>
// 	)
// }

// type TypographyInlineCodeProps = React.DetailedHTMLProps<
// 	React.HTMLAttributes<HTMLElement>,
// 	HTMLElement
// >

// export function TypographyInlineCode(props: TypographyInlineCodeProps) {
// 	return (
// 		<code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold">
// 			{props.children}
// 		</code>
// 	)
// }

// type TypographyBloquoteProps = {
// 	children: ReactNode
// }

// export function TypographyBloquote(props: TypographyBloquoteProps) {
// 	return (
// 		<blockquote className="mt-6 border-l-2 pl-6 italic">
// 			{props.children}
// 		</blockquote>
// 	)
// }

export function TypographyLead(props: TypographyPProps) {
	return (
		<p className={cn("text-xl text-muted-foreground", props.className)}>
			{props.children}
		</p>
	)
}

export function TypographyLarge(props: TypographyPProps) {
	return (
		<p className={cn("text-lg font-semibold", props.className)}>
			{props.children}
		</p>
	)
}

export function TypographySmall(props: TypographyPProps) {
	return (
		<small className={cn("text-sm font-medium leading-none", props.className)}>
			{props.children}
		</small>
	)
}

export function TypographyMuted(props: TypographyPProps) {
	return (
		<p className={cn("text-sm text-muted-foreground", props.className)}>
			{props.children}
		</p>
	)
}

// Create Admin.Typography components
// Path: app/components/admin/typography.tsx

export function TypographyAdmin({ children }: { children: ReactNode }) {
	return children
}

TypographyAdmin.H1 = function AdminH1(props: TypographyHeadingProps) {
	return (
		<h1
			{...props}
			className={cn("text-2xl font-bold text-neutral-900", props.className)}
		>
			{props.children}
		</h1>
	)
}

TypographyAdmin.H2 = function AdminH2(props: TypographyHeadingProps) {
	return (
		<h2
			{...props}
			className={cn("text-xl font-bold text-neutral-900", props.className)}
		>
			{props.children}
		</h2>
	)
}

TypographyAdmin.H3 = function AdminH3(props: TypographyHeadingProps) {
	return (
		<h3
			{...props}
			className={cn("text-lg font-bold text-neutral-900", props.className)}
		>
			{props.children}
		</h3>
	)
}

TypographyAdmin.H4 = function AdminH4(props: TypographyHeadingProps) {
	return (
		<h4
			{...props}
			className={cn("text-base font-bold text-neutral-900", props.className)}
		>
			{props.children}
		</h4>
	)
}

TypographyAdmin.SectionTitle = function AdminSectionTitle(
	props: TypographyHeadingProps,
) {
	return (
		<h2
			{...props}
			className={cn("text-sm font-medium text-neutral-900", props.className)}
		>
			{props.children}
		</h2>
	)
}

TypographyAdmin.P = function AdminP(props: TypographyPProps) {
	return (
		<p {...props} className={cn("text-base text-neutral-900", props.className)}>
			{props.children}
		</p>
	)
}

TypographyAdmin.Small = function AdminSmall(props: TypographyPProps) {
	return (
		<p {...props} className={cn("text-sm text-neutral-900", props.className)}>
			{props.children}
		</p>
	)
}

TypographyAdmin.Muted = function AdminMuted(props: TypographyPProps) {
	return (
		<p {...props} className={cn("text-sm text-neutral-500", props.className)}>
			{props.children}
		</p>
	)
}

// Create UI.Typography components

export function TypographyUI({ children }: { children: ReactNode }) {
	return children
}

TypographyUI.H1 = function UIH1(props: TypographyHeadingProps) {
	return (
		<h1
			{...props}
			className={cn("text-2xl font-bold text-neutral-900", props.className)}
		>
			{props.children}
		</h1>
	)
}
